import {ColDef, ICellRendererParams, ValueFormatterParams, ValueParserParams} from "ag-grid-community";
import AbsAGGridEditViewColumn from "@/utils/UIConstants/AGGridEditViewColumns/AbsAGGridEditViewColumn.ts";
import ValueFormatterFactory from "@/utils/UIConstants/ValueFormatterFactory.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import AGGridRichTextCellEditor from "@/components/inputs/AGGridRichTextCellEditor.vue";
import AGGridDateCellEditor from "@/components/inputs/AGGridDateCellEditor.vue";
import AGGridDateTimeCellEditor from "@/components/inputs/AGGridDateTimeCellEditor.vue";
import AGGridTimeCellEditor from "@/components/inputs/AGGridTimeCellEditor.vue";
import EditableGridHandler from "@/utils/presenters/EditableGridHandler.ts";
import AGGridBigTextCellEditor from "@/components/inputs/AGGridBigTextCellEditor.vue";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import TimeFieldUtils from "@/utils/common/TimeFieldUtils.ts";
import AGGridSuggestionProviderEditor from "@/components/inputs/AGGridSuggestionProviderEditor.vue";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import AGGridLinkCellRenderer from "@/components/inputs/AGGridLinkCellRenderer.vue";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import AGSQLEditor from "@/components/inputs/AGSQLEditor.vue";
import AGXMLEditor from "@/components/inputs/AGXMLEditor.vue";
import AGTempoEditor from "@/components/inputs/AGTempoEditor.vue";
import AGGridPhoneNumberRenderer from "@/components/inputs/AGGridPhoneNumberRenderer.vue";
import AGGridEmailRenderer from "@/components/inputs/AGGridEmailRenderer.vue";
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;

export default class AGGridEditViewTextColumn extends AbsAGGridEditViewColumn
{
  constructor(column: EditScreenGridColumn, handler: EditableGridHandler) {
    super(column, handler);
  }

  override createColDef(): ColDef[] {
    let colDef = AbsAGGridEditViewColumn.defaultColDef(this);
    let valueFormatter = ValueFormatterFactory.fromFieldType(this.columnMetadata.fieldType);
    let specs = this.createSpecs();
    colDef.valueFormatter = (params: ValueFormatterParams) => {
      return valueFormatter.format(specs, params.value);
    };
    colDef.valueParser = (params: ValueParserParams) => {
      return valueFormatter.parse(specs, params.newValue, params.oldValue);
    };

    if (this.columnMetadata.fieldType !== FieldType.BigText)
      colDef.cellRenderer = (params: ICellRendererParams) => {
        if (!params.value)
          return params.value
        else
          return AGGridUtils.createRenderedDivWithTitle(params);
      }
    if (ClientCache.isTextToPhoneNumber(this.handler.presenter, this.columnMetadata.fieldId) || this.columnMetadata.fieldType === FieldType.PhoneNumber)
      colDef.cellRenderer = AGGridPhoneNumberRenderer
    if (ClientCache.isTextToEmail(this.handler.presenter, this.columnMetadata.fieldId) || this.columnMetadata.fieldType === FieldType.Email)
      colDef.cellRenderer = AGGridEmailRenderer
    if (this.columnMetadata.fieldType === FieldType.Date) {
      colDef.cellEditor = AGGridDateCellEditor;
      colDef.cellRenderer = (params: ICellRendererParams) => AGGridUtils.createFindHighlightedDiv(params, DateFieldUtils.dateToString(params.value) ?? '');
    } else if (this.columnMetadata.fieldType === FieldType.DateAndTime) {
      colDef.cellEditor = AGGridDateTimeCellEditor;
      colDef.cellRenderer = (params: ICellRendererParams) => AGGridUtils.createFindHighlightedDiv(params, DateFieldUtils.dateAndTimeToString(params.value) ?? '');
    } else if (this.columnMetadata.fieldType === FieldType.Time) {
      colDef.cellEditor = AGGridTimeCellEditor;
      colDef.cellRenderer = (params: ICellRendererParams) => AGGridUtils.createFindHighlightedDiv(params, TimeFieldUtils.formatTime(null, params.value) ?? '');
    } else if (this.columnMetadata.fieldType === FieldType.BigText) {
      colDef.wrapText = true;
      colDef.cellEditorParams = {
        handler: () => this.handler,
        colId: this.id,
        columnMetaData: this.columnMetadata,
        lang: {
          value: BigTextFieldWidgetUtil.getDefaultCountryLanguage()
        }
      }
      colDef.cellRenderer = (params: ICellRendererParams) => {
        if (!params.valueFormatted)
          return params.valueFormatted;
        const div = AGGridUtils.createRenderedDivWithTitle(params)
        div.classList.add("tw-whitespace-nowrap", "tw-overflow-hidden", "tw-text-ellipsis")
        return div;
      }
      if (EditScreenFieldUtils.supportSQLEditor(this.columnMetadata)) {
        colDef.cellEditor = AGSQLEditor;
      } else if (EditScreenFieldUtils.supportXmlEditor(this.columnMetadata)) {
        colDef.cellEditor = AGXMLEditor;
      } else if (EditScreenFieldUtils.supportTempoEditor(this.columnMetadata)) {
        colDef.cellEditor = AGTempoEditor;
      } else if (ClientCache.isRichTextField(this.handler.entityType, this.columnMetadata.fieldId)) {
        colDef.cellEditor = AGGridRichTextCellEditor
        colDef.cellEditorPopup = true;
        colDef.cellRenderer = (params: ICellRendererParams) => {
          const div = document.createElement("div");
          div.classList.add("tw-whitespace-nowrap", "tw-overflow-hidden", "tw-text-ellipsis");
          div.innerHTML = params.valueFormatted ?? params.value ?? '';
          div.title = div.textContent ?? '';
          return div;
        };
      } else {
        colDef.cellEditor = AGGridBigTextCellEditor;
        colDef.cellEditorParams.maxLength = 1000000;
      }
    } else if (this.columnMetadata.fieldType === FieldType.Link) {
      colDef.cellRenderer = AGGridLinkCellRenderer;
      colDef.cellRendererParams = {
        handler: () => this.handler,
        columnId: this.id
      };
    } else if (this.handler.presenter.doesFieldHasSuggestionProvider(this.columnMetadata)) {
      colDef.cellEditor = AGGridSuggestionProviderEditor;
      colDef.cellEditorParams = {
        columnMetadata: this.columnMetadata,
        presenter: () => this.handler.presenter
      };
    }
    return [colDef];
  }
}
